/**
 * Utility functions for auth state management
 */

// Clear all authentication related data
export function clearAuthData() {
  // Clear cookies
  if (typeof document !== 'undefined') {
    document.cookie.split(';').forEach(cookie => {
      const [name] = cookie.trim().split('=')
      if (name) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      }
    })
  }

  // Clear localStorage
  if (typeof localStorage !== 'undefined') {
    // Remove specific items
    localStorage.removeItem('selectedUser')
    localStorage.removeItem('auth')
    
    // Look for other auth-related items
    Object.keys(localStorage).forEach(key => {
      if (key.includes('auth') || key.includes('token') || key.includes('session')) {
        localStorage.removeItem(key)
      }
    })
  }

  // Clear sessionStorage
  if (typeof sessionStorage !== 'undefined') {
    // Remove specific items
    sessionStorage.removeItem('auth')
    
    // Look for other auth-related items
    Object.keys(sessionStorage).forEach(key => {
      if (key.includes('auth') || key.includes('token') || key.includes('session')) {
        sessionStorage.removeItem(key)
      }
    })
  }
}

/**
 * Check if the current route is a public route that doesn't require authentication
 * @param path - the route path to check
 * @returns boolean - true if the route is public, false otherwise
 */
export function isPublicRoute(path: string): boolean {  // Define public routes that don't require authentication
  const publicRoutes = [
    '/login', 
    '/auth/error', 
    '/logout', 
    '/about', 
    '/contact',
    '/', // Marketing home page
    '/privacy-policy',
    '/terms-of-service',
    '/accept-terms', // Terms acceptance page
    '/help',
    '/marketing',
    '/features',
    '/pricing',
    '/guides',
    '/blog',
    '/faq',
    '/support',
    '/demo',
    '/register',
    '/signup',
    '/download',
    '/wizard', // Allow guest access to wizard
    '/resources',
    '/auth/signin',
    '/auth/signout',
    '/auth/callback',
    '/auth/verify',
    '/auth/reset-password',
    '/auth/forgot-password',
    '/auth/verify-request',
    '/auth/success',
    '/auth/error',
    '/auth/confirm',
    '/auth/new-user',    
    '/auth/welcome',
    '/auth/verify-email',
    '/auth/change-password',
    // '/choose-plan', // New subscription plan selection page
    // Additional public routes
    '/home',
    '/landing',
    '/welcome',
    '/intro',
    '/public',
    '/legal',
    '/cookies',
    '/accessibility',
    '/sitemap',
    '/error',
    '/check-email',
    '/wizard' // Allow guest access to wizard
  ]
  
  // Define public path prefixes
  const publicPathPrefixes = ['/api/', '/auth/', '/blog/']
  
  // Check if this path matches any of our public routes
  return publicRoutes.includes(path) || 
    publicPathPrefixes.some(prefix => path.startsWith(prefix))
}

/**
 * Get a safe redirect URL that avoids security issues
 * @param url - the URL to check and sanitize
 * @returns string - a safe URL to redirect to
 */
export function getSafeRedirectUrl(url: string | undefined | null): string {
  // Default to home page
  if (!url) return '/'
  
  // Only allow relative URLs to prevent open redirect vulnerabilities
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // External URLs not allowed for security reasons
    return '/'
  }
  
  return url
}
